import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { GatsbyImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography";
import { when } from "../../styles/theme";

export const fragment = graphql`
  fragment CompanyInfoContent on ContentfulPageSectionCompanyInfo {
    name
    title
    subtitle
    address {
      childMarkdownRemark {
        html
      }
    }
    phone
    addressLink
    image {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
    }

    internal {
      type
    }
  }
`;

const CompanyInfoSection = styled.section`
  padding: 8rem 0;

  ${when("md")} {
    padding: 15rem 0;
  }
`;

const ContentSection = styled(PageContainer)`
  margin-top: 5rem;
  &:first-child {
    margin-top: 0;
  }

  ${when("md")} {
    display: flex;
    align-items: center;
    column-gap: ${(props) => props.theme.gutterWidth.md}rem;
    max-width: 99.6rem;
    justify-content: space-between;
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }
`;

const Title = styled(SectionTitle)`
  margin-top: 5rem;
  font-size: 2.8rem;
  line-height: 1.5;
  color: ${(props) => props.theme.colors.red};
  ${when("md")} {
    font-size: 4.8rem;
    margin-top: 0;
  }
`;

const Subtitle = styled.div`
  margin-top: 0.3125rem;
  font-size: 2rem;
  line-height: 1.5;
  font-weight: 700;

  ${when("md")} {
    margin-top: 1.6rem;
    font-size: 2.8rem;
  }
`;

const Description = styled.a`
  margin-top: 0.5rem;
  display: block;
  max-width: 50%;
  text-align: center;

  ${when("md")} {
    margin-top: 1rem;
    max-width: 24rem;
    text-align: left;
  }
`;

const ImageContainer = styled.div``;
const ImageWrapper = styled.div`
  position: relative;

  max-width: 23rem;

  margin: 0 auto;

  ${when("md")} {
    max-width: 37rem;
  }
`;

const Circle = styled.div`
  position: absolute;
  width: 80%;
  height: 80%;
  bottom: -0.5rem;
  right: -0.5rem;
  background-color: ${(props) => props.theme.colors.red};
  border-radius: 9999rem;
`;

const Image = styled(GatsbyImage)`
  overflow: hidden;
  border-radius: 50%;
  transform: translateZ(0);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${when("md")} {
    width: 30rem;
    align-items: flex-start;
  }

  ${when("lg")} {
    width: 40rem;
  }
`;

const CompanyInfo = ({ data }) => {
  const { image, title, subtitle, phone, address, addressLink } = data;

  return (
    <CompanyInfoSection>
      <ContentSection>
        <ImageContainer>
          <ImageWrapper>
            <Circle />
            <Image image={image?.gatsbyImageData} alt={image?.description} />
          </ImageWrapper>
        </ImageContainer>
        <Content>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <Description
            as={"a"}
            href={addressLink}
            target={"_blank"}
            dangerouslySetInnerHTML={{
              __html: address.childMarkdownRemark.html,
            }}
          />
          <Description href={`tel:${phone}`}>{phone}</Description>
        </Content>
      </ContentSection>
    </CompanyInfoSection>
  );
};
export const companyInfoName = "ContentfulPageSectionCompanyInfo";

export default CompanyInfo;
